// Tiny DOM helpers and the strings lookup. Every visible text comes from strings.de.json via t().
import type { BootData } from '../shared/types.js';

export const boot: BootData = JSON.parse(document.getElementById('boot')!.textContent || '{}') as BootData;

type Child = Node | string | number | null | undefined | false | Child[];

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, unknown> = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === null || value === undefined || value === false) continue;
    if (key === 'class') el.className = String(value);
    else if (key === 'html') el.innerHTML = String(value);
    else if (key.startsWith('on') && typeof value === 'function') el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    else if (key === 'hidden') el.hidden = Boolean(value);
    else if (key === 'disabled') (el as HTMLButtonElement).disabled = Boolean(value);
    else if (key === 'checked') (el as HTMLInputElement).checked = Boolean(value);
    else if (key === 'value') (el as HTMLInputElement).value = String(value);
    else el.setAttribute(key, String(value));
  }
  append(el, children);
  return el;
}

function append(el: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    if (Array.isArray(child)) append(el, child);
    else if (child instanceof Node) el.appendChild(child);
    else el.appendChild(document.createTextNode(String(child)));
  }
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

function lookup(path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>) ? (acc as Record<string, unknown>)[key] : undefined), boot.strings);
}

export function t(path: string, vars: Record<string, string | number> = {}): string {
  const raw = lookup(path);
  const text = typeof raw === 'string' ? raw : path;
  return text.replace(/\{(\w+)\}/g, (_, key: string) => (key in vars ? String(vars[key]) : `{${key}}`));
}

export function tl(path: string): string[] {
  const raw = lookup(path);
  return Array.isArray(raw) ? (raw as string[]) : [];
}

export function tobj<T = Record<string, unknown>>(path: string): T {
  const raw = lookup(path);
  return (raw && typeof raw === 'object' ? raw : {}) as T;
}

/** «…» around a quote unless it already carries quotation marks. */
export function quoted(text: string): string {
  const trimmed = text.trim();
  return /^[«"“]/.test(trimmed) ? trimmed : `«${trimmed}»`;
}

export function scrollTop(): void {
  window.scrollTo({ top: 0 });
}

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getDate()}. ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
